import React, { useEffect, useRef, useState } from "react";
import { Dna, FlaskConical, Cog } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/slqogudy_Emblem.jpeg";

const NODES = [
    {
        id: "biology",
        label: "Biology",
        Icon: Dna,
        angle: -90,
        copy: "Living systems · enzymes · microbes",
    },
    {
        id: "chemistry",
        label: "Chemistry",
        Icon: FlaskConical,
        angle: 30,
        copy: "Molecules · reactions · formulations",
    },
    {
        id: "engineering",
        label: "Engineering",
        Icon: Cog,
        angle: 150,
        copy: "Process · scale · industrial reality",
    },
];

const RADIUS = 38; // % of container

const InteractiveAtom = () => {
    const wrapRef = useRef(null);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const [active, setActive] = useState(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const handle = (e) => {
            const r = el.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            setTilt({ x: px * 14, y: py * 14 });
        };
        const reset = () => setTilt({ x: 0, y: 0 });
        el.addEventListener("mousemove", handle);
        el.addEventListener("mouseleave", reset);
        return () => {
            el.removeEventListener("mousemove", handle);
            el.removeEventListener("mouseleave", reset);
        };
    }, []);

    return (
        <div
            ref={wrapRef}
            data-testid="hero-interactive-atom"
            className="relative aspect-square w-full max-w-[520px] mx-auto select-none"
            style={{
                transformStyle: "preserve-3d",
                perspective: "1200px",
            }}
        >
            <div
                className="relative w-full h-full transition-transform duration-300 ease-out"
                style={{
                    transform: `rotateY(${tilt.x}deg) rotateX(${-tilt.y}deg)`,
                    transformStyle: "preserve-3d",
                }}
            >
                {/* Concentric ornamental rings */}
                <svg
                    viewBox="0 0 400 400"
                    aria-hidden
                    className="absolute inset-0 w-full h-full"
                >
                    <defs>
                        <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
                            <stop
                                offset="0%"
                                stopColor="hsl(var(--brand-copper))"
                                stopOpacity="0.45"
                            />
                            <stop
                                offset="60%"
                                stopColor="hsl(var(--brand-copper))"
                                stopOpacity="0.08"
                            />
                            <stop
                                offset="100%"
                                stopColor="hsl(var(--brand-copper))"
                                stopOpacity="0"
                            />
                        </radialGradient>
                        <linearGradient
                            id="strandA"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="hsl(var(--brand-copper))"
                            />
                            <stop
                                offset="100%"
                                stopColor="hsl(var(--brand-moss))"
                            />
                        </linearGradient>
                        <linearGradient
                            id="strandB"
                            x1="0"
                            y1="1"
                            x2="0"
                            y2="0"
                        >
                            <stop
                                offset="0%"
                                stopColor="hsl(var(--brand-moss))"
                            />
                            <stop
                                offset="100%"
                                stopColor="hsl(var(--brand-copper))"
                            />
                        </linearGradient>
                    </defs>

                    {/* Core glow */}
                    <circle cx="200" cy="200" r="120" fill="url(#coreGlow)" />

                    {/* Rings */}
                    <g className="text-primary/30">
                        <circle
                            cx="200"
                            cy="200"
                            r="190"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="0.6"
                            strokeDasharray="2 6"
                        />
                        <circle
                            cx="200"
                            cy="200"
                            r="155"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="0.5"
                        />
                        <circle
                            cx="200"
                            cy="200"
                            r="120"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="0.4"
                            strokeDasharray="1 4"
                        />
                    </g>

                    {/* DNA double helix — animated */}
                    <g
                        style={{
                            transformOrigin: "200px 200px",
                            animation: "spinSlow 22s linear infinite",
                        }}
                    >
                        <path
                            d="M200 80 Q 240 130 200 200 Q 160 270 200 320"
                            fill="none"
                            stroke="url(#strandA)"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        />
                        <path
                            d="M200 80 Q 160 130 200 200 Q 240 270 200 320"
                            fill="none"
                            stroke="url(#strandB)"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        />
                        {/* Rungs */}
                        {Array.from({ length: 9 }).map((_, i) => {
                            const t = i / 8;
                            const y = 80 + t * 240;
                            const off = Math.sin(t * Math.PI * 2) * 28;
                            return (
                                <line
                                    key={i}
                                    x1={200 - off}
                                    x2={200 + off}
                                    y1={y}
                                    y2={y}
                                    stroke="hsl(var(--brand-copper))"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    opacity="0.55"
                                />
                            );
                        })}
                    </g>

                    {/* Floating molecule dots */}
                    <g
                        style={{
                            transformOrigin: "200px 200px",
                            animation: "spinReverse 30s linear infinite",
                        }}
                    >
                        {[
                            [80, 200],
                            [320, 200],
                            [200, 60],
                            [200, 340],
                            [120, 110],
                            [280, 290],
                        ].map(([cx, cy], i) => (
                            <circle
                                key={i}
                                cx={cx}
                                cy={cy}
                                r="3"
                                fill="hsl(var(--brand-copper))"
                                opacity="0.7"
                            />
                        ))}
                    </g>
                </svg>

                {/* Center — Yaalee logo emblem */}
                <div
                    className="absolute inset-[30%] rounded-full bg-white border border-border shadow-2xl shadow-primary/20 overflow-hidden"
                    style={{ transform: "translateZ(40px)" }}
                >
                    <img
                        src={LOGO_URL}
                        alt="Yaalee Biotech Solutions emblem"
                        className="absolute inset-0 w-full h-full object-cover scale-110"
                    />
                    <span
                        aria-hidden
                        className="absolute inset-0 rounded-full ring-pulse"
                    />
                </div>

                {/* Orbital nodes */}
                {NODES.map(({ id, label, Icon, angle, copy }) => {
                    const rad = (angle * Math.PI) / 180;
                    const left = 50 + RADIUS * Math.cos(rad);
                    const top = 50 + RADIUS * Math.sin(rad);
                    const isActive = active === id;
                    return (
                        <button
                            key={id}
                            data-testid={`hero-node-${id}`}
                            onMouseEnter={() => setActive(id)}
                            onMouseLeave={() => setActive(null)}
                            onFocus={() => setActive(id)}
                            onBlur={() => setActive(null)}
                            className="absolute group focus:outline-none"
                            style={{
                                left: `${left}%`,
                                top: `${top}%`,
                                transform: "translate(-50%, -50%) translateZ(60px)",
                            }}
                            aria-label={`${label}: ${copy}`}
                        >
                            {/* outer halo */}
                            <span
                                aria-hidden
                                className={`absolute inset-0 rounded-full transition-all duration-500 ${
                                    isActive
                                        ? "scale-[1.6] bg-primary/25 blur-md"
                                        : "scale-100 bg-primary/0"
                                }`}
                            />
                            <span
                                className={`relative flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-full border bg-background shadow-md transition-all duration-300 ${
                                    isActive
                                        ? "border-primary text-primary-foreground bg-primary scale-110"
                                        : "border-border text-primary hover:border-primary/60"
                                }`}
                            >
                                <Icon className="h-5 w-5 md:h-6 md:w-6" />
                            </span>
                            <span className="absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap text-[10px] uppercase tracking-[0.22em] font-semibold text-foreground/70">
                                {label}
                            </span>

                            {/* tooltip on active */}
                            <span
                                role="tooltip"
                                className={`pointer-events-none absolute left-1/2 -translate-x-1/2 -top-14 whitespace-nowrap rounded-full border border-border bg-card/95 backdrop-blur-md px-3.5 py-1.5 text-[11px] text-card-foreground shadow-lg transition-all duration-300 ${
                                    isActive
                                        ? "opacity-100 -translate-y-1"
                                        : "opacity-0 translate-y-1"
                                }`}
                            >
                                {copy}
                            </span>
                        </button>
                    );
                })}

                {/* Connector lines from center to nodes (decorative) */}
                <svg
                    viewBox="0 0 100 100"
                    aria-hidden
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    preserveAspectRatio="none"
                >
                    {NODES.map(({ id, angle }) => {
                        const rad = (angle * Math.PI) / 180;
                        const x = 50 + RADIUS * Math.cos(rad);
                        const y = 50 + RADIUS * Math.sin(rad);
                        const isActive = active === id;
                        return (
                            <line
                                key={id}
                                x1="50"
                                y1="50"
                                x2={x}
                                y2={y}
                                stroke="hsl(var(--brand-copper))"
                                strokeOpacity={isActive ? "0.7" : "0.25"}
                                strokeWidth={isActive ? "0.4" : "0.2"}
                                strokeDasharray="0.6 1.4"
                                vectorEffect="non-scaling-stroke"
                                className="transition-all duration-500"
                            />
                        );
                    })}
                </svg>
            </div>

            {/* Hint chip */}
            <div className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Hover the disciplines · move your cursor to tilt
            </div>
        </div>
    );
};

export default InteractiveAtom;
